import React, { useState } from 'react';
import { Ban, Flag, MoreHorizontal } from 'lucide-react';
import { RADIUS, TYPE, useTheme } from '../../theme';
import { Button, Dialog, Field, Icon, IconButton, Menu, Textarea } from '../ui';
import { modApi, REASONS, type ModTarget, type Reason } from './moderationApi';

// AS1c · "⋯"-meny på andras innehåll: Rapportera… och Blockera. Används i chatt, album,
// jaktmedia, annonser och medlemslistan. API:t avgör vem som äger innehållet.

export interface ReportMenuProps {
  target: ModTarget;
  /** Namnet som visas i "Blockera Arne". */
  ownerName?: string | null;
  /** Vad som rapporteras, i menyn och rubriken ("meddelandet", "bilden", "annonsen", "profilen"). */
  noun?: string;
  /** Efter blockering: dölj innehållet direkt. Får e-postadressen som API:t blockerade. */
  onBlocked?: (email: string) => void;
  /** Knappen på mörk bakgrund (bildgalleriet). */
  onDark?: boolean;
  /** Ingen blockering (t.ex. när innehållet saknar en person). */
  noBlock?: boolean;
  size?: number;
  /** Menyn öppnas uppåt (knappen längst ner på skärmen). */
  up?: boolean;
  /** Menyn växer åt höger (knappen sitter till vänster på skärmen). */
  alignLeft?: boolean;
}

export function ReportMenu({ target, ownerName, noun = 'innehållet', onBlocked, onDark, noBlock, size = 36, up, alignLeft }: ReportMenuProps) {
  const T = useTheme();
  const [dialog, setDialog] = useState<'report' | 'block' | null>(null);
  const who = ownerName?.trim() || 'användaren';
  return (
    <>
      <Menu align={alignLeft ? 'left' : 'right'} width={250} placement={up ? 'top' : 'bottom'}
        trigger={p => (
          <IconButton {...p} label={`Mer om ${noun}`} size={size} onClick={e => { e.stopPropagation(); p.onClick(); }}
            style={{ width: size, height: size, minWidth: 44, minHeight: 44, color: onDark ? '#fff' : T.textMuted, background: onDark ? 'rgba(255,255,255,0.16)' : 'transparent' }}>
            <Icon as={MoreHorizontal} size={18} />
          </IconButton>
        )}
        items={[
          { key: 'report', label: `Rapportera ${noun}…`, icon: <Icon as={Flag} size={18} />, onSelect: () => setDialog('report') },
          ...(noBlock ? [] : [{ key: 'block', label: `Blockera ${who}`, icon: <Icon as={Ban} size={18} />, danger: true, onSelect: () => setDialog('block') }]),
        ]} />
      {dialog === 'report' && <ReportDialog target={target} noun={noun} ownerName={noBlock ? null : ownerName}
        onClose={() => setDialog(null)} onBlock={() => setDialog('block')} />}
      {dialog === 'block' && <BlockDialog target={target} ownerName={who} onClose={() => setDialog(null)} onBlocked={onBlocked} />}
    </>
  );
}

export function ReportDialog({ target, noun, ownerName, onClose, onBlock }: {
  target: ModTarget; noun: string; ownerName?: string | null; onClose: () => void; onBlock?: () => void;
}) {
  const T = useTheme();
  const [reason, setReason] = useState<Reason | null>(null);
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);

  async function send() {
    if (!reason) { setError('Välj en orsak'); return; }
    if (reason === 'other' && !details.trim()) { setError('Beskriv vad det gäller'); return; }
    setBusy(true); setError('');
    try { await modApi.report(target, reason, details.trim()); setSent(true); }
    catch (e: any) { setError(e.message); }
    finally { setBusy(false); }
  }

  if (sent) {
    return (
      <Dialog open onClose={onClose} title="Tack för att du säger till"
        actions={<>
          {ownerName && onBlock && <Button variant="secondary" onClick={onBlock}>Blockera {ownerName}</Button>}
          <Button onClick={onClose}>Klar</Button>
        </>}>
        <p role="status" style={{ ...TYPE.body, color: T.textSub, margin: 0 }}>
          Anmälan har skickats till Qvenari och vi går igenom den så snart vi kan. Den du anmält får inte veta vem som anmälde.
          {ownerName ? ` Vill du slippa se mer från ${ownerName} kan du också blockera hen.` : ''}
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={onClose} title={`Rapportera ${noun}`}
      actions={<>
        <Button variant="secondary" onClick={onClose}>Avbryt</Button>
        <Button onClick={send} loading={busy}>Skicka anmälan</Button>
      </>}>
      <p style={{ ...TYPE.small, color: T.textSub, margin: '0 0 12px' }}>Varför ska Qvenari titta på det här?</p>
      <div role="radiogroup" aria-label="Orsak" style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
        {REASONS.map(r => {
          const on = reason === r.value;
          return (
            <label key={r.value} style={{
              display: 'flex', alignItems: 'center', gap: 12, minHeight: 52, padding: '8px 12px', cursor: 'pointer',
              borderRadius: RADIUS.md, border: `1px solid ${on ? T.accentFill : T.border}`, background: on ? T.accentSoft : 'transparent',
            }}>
              <input type="radio" name="report-reason" value={r.value} checked={on} onChange={() => { setReason(r.value); setError(''); }}
                style={{ width: 18, height: 18, accentColor: T.accentFill, margin: 0, flexShrink: 0 }} />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: 'block', color: T.text, fontWeight: 700, fontSize: 15 }}>{r.label}</span>
                <span style={{ display: 'block', color: T.textMuted, fontSize: 13 }}>{r.sub}</span>
              </span>
            </label>
          );
        })}
      </div>
      <Field label={reason === 'other' ? 'Beskriv vad det gäller' : 'Mer information (valfritt)'} error={error || undefined}>
        <Textarea value={details} onChange={e => setDetails(e.target.value)} rows={3} maxLength={2000} placeholder="Skriv här" />
      </Field>
    </Dialog>
  );
}

function BlockDialog({ target, ownerName, onClose, onBlocked }: { target: ModTarget; ownerName: string; onClose: () => void; onBlocked?: (email: string) => void }) {
  const T = useTheme();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function block() {
    setBusy(true); setError('');
    try {
      const r = await modApi.block(target);
      onClose();
      onBlocked?.(r.email);
    } catch (e: any) { setError(e.message); setBusy(false); }
  }
  return (
    <Dialog open onClose={onClose} title={`Blockera ${ownerName}?`}
      actions={<>
        <Button variant="secondary" onClick={onClose}>Avbryt</Button>
        <Button variant="danger" onClick={block} loading={busy}>Blockera</Button>
      </>}>
      <p style={{ ...TYPE.body, color: T.textSub, margin: 0 }}>
        Du slutar se {ownerName}s meddelanden, bilder, annonser och profil i Qvenari. {ownerName} får inte veta att du blockerat hen.
        Du kan häva blockeringen under Profil → Blockerade användare.
      </p>
      {error && <p role="alert" style={{ ...TYPE.small, color: T.danger, fontWeight: 600, margin: '12px 0 0' }}>{error}</p>}
    </Dialog>
  );
}
